// FLAGS: none
// "Withdraw as host" on a scheduled session the expert hosts: confirms, then hands the session (and its paired part)
// back to substitutes, which tells the scheduling channel. The API refuses it once the session starts. A button by
// default; My sessions renders it as an entry of the row's actions menu instead (renderTrigger).
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@openup/shared/components/Button/Button";
import ConfirmActionDialog from "@src/components/ConfirmActionDialog";
import { useWithdrawAsHostMutation } from "@src/queries/spaces";
import hostSignupErrorKey from "@src/views/HostSignups/hostSignupError";

interface WithdrawAsHostButtonProps {
  session: { sessionId: string; title: string };
  /**
   * Renders what opens the confirmation in place of the button, such as a menu entry. The confirmation stays here,
   * outside whatever the trigger renders, so it survives a menu closing.
   */
  renderTrigger?: (askToConfirm: () => void, busy: boolean) => ReactNode;
}

const WithdrawAsHostButton = ({
  session,
  renderTrigger = undefined,
}: WithdrawAsHostButtonProps) => {
  const { sessionId, title } = session;
  const { t } = useTranslation();
  const withdraw = useWithdrawAsHostMutation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setError(null);
    try {
      await withdraw.mutateAsync(sessionId);
      setConfirming(false);
    } catch (caught) {
      setConfirming(false);
      setError(t(hostSignupErrorKey(caught)));
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      {renderTrigger ? (
        renderTrigger(() => setConfirming(true), withdraw.isPending)
      ) : (
        <Button
          variant="quaternary"
          size="small"
          disabled={withdraw.isPending}
          onClick={() => setConfirming(true)}
        >
          {t("HostSignups.Withdraw.Action")}
        </Button>
      )}
      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
      <ConfirmActionDialog
        open={confirming}
        title={t("HostSignups.Withdraw.ConfirmTitle")}
        description={t("HostSignups.Withdraw.ConfirmBody", { title })}
        submitting={withdraw.isPending}
        onConfirmed={confirm}
        onCancelled={() => setConfirming(false)}
      />
    </div>
  );
};

export default WithdrawAsHostButton;
